import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import quantService from '../../../services/quantService';

// Validation page is reached via /trading/quant/validation/<jobId>.
// It re-renders the alphalens results from the job row (already persisted
// during the runAlphalens call) — no separate "validation job" exists.

const fmt = (v, n = 4) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);

const ValidationPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    quantService.getDiscovery(jobId)
      .then(({ data }) => mounted && setJob(data))
      .catch(e => mounted && setError(e?.response?.data?.detail || e.message));
    return () => { mounted = false; };
  }, [jobId]);

  if (error) return <div className="p-6 text-sm text-red-500">{error}</div>;
  if (!job) return <div className="p-6 text-sm text-gray-400">Loading…</div>;
  if (!job.alphalens_attempted) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        Alphalens has not been run on this job yet.<br />
        <button onClick={() => navigate(`/trading/quant/jobs/${jobId}`)}
                className="text-brand-gold underline">
          Open job detail to run validation
        </button>
      </div>
    );
  }

  const r = job.alphalens_results || {};
  const passed = !!job.alphalens_passed;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/trading/quant/jobs/${jobId}`)}
                className="text-xs uppercase tracking-wider text-gray-500 hover:text-brand-gold">
          ← Back to discovery
        </button>
        <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded ${
          passed ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20'
                  : 'bg-red-50 text-red-500 dark:bg-red-900/20'
        }`}>
          {passed ? 'PASSED' : 'FAILED'}
        </span>
      </div>

      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Kill Gate Checklist
          </h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Gate</th>
              <th className="text-right px-4 py-2">Threshold</th>
              <th className="text-right px-4 py-2">Actual</th>
              <th className="text-center px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {r.gates && Object.entries(r.gates).map(([name, g]) => (
              <tr key={name} className="border-t border-gray-100 dark:border-gray-800/50">
                <td className="px-4 py-2 font-mono text-xs">{name}</td>
                <td className="px-4 py-2 text-right text-xs font-mono text-gray-500">{fmt(g.threshold, 3)}</td>
                <td className={`px-4 py-2 text-right text-xs font-mono ${g.passed ? 'text-emerald-600' : 'text-red-500'}`}>
                  {fmt(g.actual, 4)}
                </td>
                <td className="px-4 py-2 text-center text-lg">
                  {g.passed ? <span className="text-emerald-500">✓</span> : <span className="text-red-500">✗</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {r.monotonic === false && (
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 text-xs text-amber-500">
            ⚠ Quintile returns not fully monotonic. Q5–Q1 spread is the primary gate; Q2/Q3/Q4 dips are informational only.
          </div>
        )}
      </div>

      {r.mean_return_by_quantile && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
            Mean Return by Quintile (21d horizon)
          </h2>
          <div className="grid grid-cols-5 gap-2">
            {Object.entries(r.mean_return_by_quantile['21'] || r.mean_return_by_quantile[Object.keys(r.mean_return_by_quantile)[0]] || {})
              .sort((a, b) => Number(a[0]) - Number(b[0]))
              .map(([q, ret]) => (
                <div key={q} className="text-center bg-gray-50 dark:bg-gray-800/50 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">Q{Number(q) + 1}</div>
                  <div className={`text-sm font-mono ${ret >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {fmt(ret * 100, 2)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationPage;
